<template>
  <main class="login-page">
    <section class="login-panel">
      <LanguageSwitcher class="language-switcher--panel" />
      <div class="brand-lockup">
        <img class="brand-mark" :src="platformLogo" :alt="language.t('auth.iconAlt', { name: platformName })" @error="handleBrandLogoError">
        <div>
          <h1>{{ platformName }}</h1>
          <p>{{ language.t('app.subtitle') }}</p>
        </div>
      </div>

      <form v-if="view === 'login'" class="login-form" @submit.prevent="submit">
        <div class="login-tabs">
          <button type="button" :class="{ active: mode === 'password' }" @click="setLoginMode('password')">{{ language.t('auth.passwordLogin') }}</button>
          <button type="button" :class="{ active: mode === 'code' }" @click="setLoginMode('code')">{{ language.t('auth.codeLogin') }}</button>
        </div>

        <label>
          <span>{{ mode === 'password' ? '手机号 / 邮箱 / 用户名' : language.t('auth.phone') }}</span>
          <input v-model.trim="mobile" autocomplete="username" :inputmode="mode === 'password' ? 'text' : 'tel'" :placeholder="mode === 'password' ? '请输入手机号、邮箱或用户名' : language.t('auth.phonePlaceholder')">
        </label>
        <label v-if="mode === 'password'">
          <span>{{ language.t('auth.password') }}</span>
          <input v-model="password" autocomplete="current-password" type="password" :placeholder="language.t('auth.passwordPlaceholder')">
        </label>
        <label v-else>
          <span>{{ language.t('auth.code') }}</span>
          <div class="code-row">
            <input v-model.trim="code" autocomplete="one-time-code" inputmode="numeric" :placeholder="language.t('auth.localCodePlaceholder')">
            <button type="button" class="code-action" :disabled="sendingCode || mobile.length < 5" @click="requestCode">
              {{ sendingCode ? language.t('auth.sending') : language.t('auth.sendCode') }}
            </button>
          </div>
        </label>
        <button class="primary-action" :disabled="session.connecting || !canSubmit">
          {{ session.connecting ? language.t('auth.loggingIn') : language.t('auth.login') }}
        </button>
        <p v-if="sessionError" class="form-error">{{ sessionError }}</p>
        <p v-if="info" class="form-info">{{ info }}</p>
      </form>

      <form v-else-if="view === 'register'" class="login-form" @submit.prevent="submitRegister">
        <p v-if="registrationLoading" class="form-info">正在读取注册方式…</p>
        <p v-else-if="!visibleRegisterMethods.length" class="form-info">当前未开放新用户注册</p>
        <div class="login-tabs register-tabs" :style="{ gridTemplateColumns: `repeat(${visibleRegisterMethods.length || 1}, minmax(0, 1fr))` }">
          <button
            v-for="item in visibleRegisterMethods"
            :key="item.key"
            type="button"
            :class="{ active: registerType === item.key }"
            @click="registerType = item.key"
          >
            {{ item.label }}
          </button>
        </div>

        <label v-if="registerType === 'phone'">
          <span class="label-row">
            <span>{{ language.t('auth.phone') }}</span>
            <span v-if="registerPhoneError" class="field-error-badge">{{ registerPhoneError }}</span>
          </span>
          <div class="phone-row">
            <div class="country-code-field">
              <span>+</span>
              <input
                v-model.trim="registerForm.phoneCountryCode"
                :aria-label="language.t('auth.phoneCountryCode')"
                autocomplete="tel-country-code"
                inputmode="numeric"
                maxlength="6"
                @input="normalizeRegisterCountryCode"
              >
            </div>
            <input
              v-model.trim="registerForm.phone"
              :class="{ 'field-input-error': registerPhoneError }"
              autocomplete="tel"
              inputmode="tel"
              :placeholder="language.t('auth.phonePlaceholder')"
              @input="clearRegisterPhoneError"
            >
          </div>
        </label>
        <label v-if="registerType === 'phone'">
          <span>{{ language.t('auth.code') }}</span>
          <div v-if="mobileRealVerify" class="code-row">
            <input v-model.trim="registerForm.code" autocomplete="one-time-code" inputmode="numeric" :placeholder="language.t('auth.smsCodePlaceholder')">
            <button type="button" class="code-action" :disabled="sendingCode || registerForm.phone.length < 5" @click="requestRegisterCode">
              {{ sendingCode ? language.t('auth.sending') : language.t('auth.sendCode') }}
            </button>
          </div>
          <div v-else class="captcha-row">
            <input
              v-model.trim="registerForm.code"
              :class="phoneCaptchaClass"
              autocomplete="off"
              inputmode="numeric"
              maxlength="4"
              :placeholder="language.t('auth.codePlaceholder')"
              @input="normalizePhoneCaptchaInput"
            >
            <button v-if="!phoneCaptchaImage" type="button" class="code-action" :disabled="sendingCode || registerForm.phone.length < 5" @click="showPhoneCaptcha">
              {{ language.t('auth.sendCode') }}
            </button>
            <button v-else type="button" class="captcha-button" :title="language.t('auth.refreshCaptcha')" :disabled="sendingCode || registerForm.phone.length < 5" @click="showPhoneCaptcha">
              <img :src="phoneCaptchaImage" :alt="language.t('auth.captchaAlt')">
            </button>
          </div>
        </label>

        <label v-if="emailRegisterEnabled && registerType === 'email'">
          <span>{{ language.t('auth.email') }}</span>
          <input v-model.trim="registerForm.email" autocomplete="email" inputmode="email" :placeholder="language.t('auth.emailPlaceholder')">
        </label>
        <label v-if="emailRegisterEnabled && registerType === 'email'">
          <span>{{ language.t('auth.emailCode') }}</span>
          <div class="code-row">
            <input v-model.trim="registerForm.emailCode" autocomplete="one-time-code" inputmode="numeric" :placeholder="language.t('auth.codePlaceholder')">
            <button type="button" class="code-action" :disabled="sendingCode || !registerForm.email" @click="requestEmailCode">{{ sendingCode ? language.t('auth.sending') : language.t('auth.sendCode') }}</button>
          </div>
        </label>

        <label v-if="registerType === 'username'">
          <span>{{ language.t('auth.username') }}</span>
          <input v-model.trim="registerForm.username" autocomplete="username" :placeholder="language.t('auth.usernamePlaceholder')">
        </label>
        <label v-if="registerType === 'username'">
          <span>{{ language.t('auth.code') }}</span>
          <div class="captcha-row">
            <input
              v-model.trim="registerForm.usernameCode"
              :class="usernameCaptchaClass"
              autocomplete="off"
              inputmode="numeric"
              maxlength="4"
              :placeholder="language.t('auth.codePlaceholder')"
              @input="normalizeUsernameCaptchaInput"
            >
            <button v-if="!usernameCaptchaImage" type="button" class="code-action" :disabled="sendingCode || registerForm.username.length < 3" @click="showUsernameCaptcha">
              {{ language.t('auth.showCaptcha') }}
            </button>
            <button v-else type="button" class="captcha-button" :title="language.t('auth.refreshCaptcha')" :disabled="sendingCode || registerForm.username.length < 3" @click="showUsernameCaptcha">
              <img :src="usernameCaptchaImage" :alt="language.t('auth.captchaAlt')">
            </button>
          </div>
        </label>

        <label v-if="visibleRegisterMethods.length">
          <span>{{ language.t('auth.password') }}</span>
          <input v-model="registerForm.password" autocomplete="new-password" type="password" minlength="6" maxlength="128" placeholder="至少 6 位字符">
        </label>
        <label v-if="visibleRegisterMethods.length">
          <span>{{ language.t('auth.confirmPassword') }}</span>
          <input v-model="registerForm.confirmPassword" autocomplete="new-password" type="password" :placeholder="language.t('auth.confirmPasswordPlaceholder')">
        </label>

        <button class="primary-action" :disabled="registering || sendingCode || registrationLoading || !canRegisterSubmit">
          {{ registering ? language.t('auth.registering') : language.t('auth.register') }}
        </button>
        <p v-if="sessionError" class="form-error">{{ sessionError }}</p>
        <p v-if="info" class="form-info">{{ info }}</p>
      </form>

      <form v-else class="login-form" @submit.prevent="submitRegisterPassword">
        <label>
          <span>{{ language.t('auth.phone') }}</span>
          <input :value="pendingRegistration.mobile" autocomplete="username" readonly>
        </label>
        <label>
          <span>{{ language.t('auth.loginPassword') }}</span>
          <input v-model="registerPasswordForm.password" autocomplete="new-password" type="password" :placeholder="language.t('auth.loginPasswordPlaceholder')">
        </label>
        <label>
          <span>{{ language.t('auth.confirmPassword') }}</span>
          <input v-model="registerPasswordForm.confirmPassword" autocomplete="new-password" type="password" :placeholder="language.t('auth.confirmPasswordPlaceholder')">
        </label>
        <button class="primary-action" :disabled="session.connecting || settingRegisterPassword || !canSetRegisterPassword">
          {{ settingRegisterPassword || session.connecting ? language.t('common.saving') : language.t('auth.saveAndEnter') }}
        </button>
        <p v-if="sessionError" class="form-error">{{ sessionError }}</p>
        <p v-if="info" class="form-info">{{ info }}</p>
      </form>

      <button v-if="view === 'login'" class="ghost-action" type="button" @click="openRegister">
        {{ language.t('auth.newUserRegister') }}
      </button>
      <button v-else-if="view === 'register'" class="ghost-action" type="button" @click="backToLogin">
        {{ language.t('auth.backToLogin') }}
      </button>
    </section>
  </main>
</template>

<script setup>
import { projectBrand, loadProjectBrand } from '@/services/projectBrand';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { appConfig } from '@/config';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import { useSessionStore } from '@/stores/session';
import { useLanguageStore } from '@/stores/language';
import { registrationRequest } from '@/services/registration';
import { imClient } from '@/services/imClient';

const session = useSessionStore();
const language = useLanguageStore();
const view = ref('login');
const mode = ref('password');
const mobile = ref('');
const password = ref('');
const code = ref('666666');
const sendingCode = ref(false);
const info = ref('');
const pendingRegistrationStorageKey = 'qmt-web-im-pending-registration';
const defaultPlatformLogo = '/img/milian-logo.png';
const platformName = computed(() => projectBrand.name);
const platformLogo = computed(() => projectBrand.icon_url);
const registerType = ref('phone');
const emailRegisterEnabled = true;
const registering = ref(false);
const registrationLoading = ref(true);
const emailChallenge = ref('');
const registerMethods = ref({ phone: false, email: false, username: false });
const mobileRealVerify = ref(false);
const registerPhoneError = ref('');
const phoneCaptcha = ref('');
const phoneCaptchaImage = ref('');
const usernameCaptcha = ref('');
const usernameCaptchaImage = ref('');
const settingRegisterPassword = ref(false);
const pendingRegistration = reactive({
  mobile: '',
  result: null,
  resetCode: ''
});
const registerForm = reactive({
  phoneCountryCode: '86',
  phone: '',
  code: '',
  email: '',
  emailCode: '',
  username: '',
  usernameCode: '',
  password: '',
  confirmPassword: ''
});
const registerPasswordForm = reactive({
  password: '',
  confirmPassword: ''
});
const registerMethodOptions = computed(() => [
  { key: 'phone', label: language.t('auth.phoneRegister') },
  { key: 'email', label: language.t('auth.emailRegister') },
  { key: 'username', label: language.t('auth.usernameRegister') }
]);
const canSubmit = computed(() => {
  if (mode.value === 'password') {
    return mobile.value.length >= 3 && password.value.length >= 1;
  }
  return mobile.value.length >= 5 && code.value.length >= 1;
});
const sessionError = computed(() => {
  return session.errorKey ? language.t(session.errorKey) : session.error;
});
const visibleRegisterMethods = computed(() => registerMethodOptions.value.filter(item => registerMethods.value[item.key]));
const canRegisterSubmit = computed(() => {
  if (!visibleRegisterMethods.value.length || registerForm.password.length < 6 || registerForm.password !== registerForm.confirmPassword) return false;
  if (registerType.value === 'phone') return registerForm.phone.length >= 5 && (mobileRealVerify.value ? phoneCaptcha.value && /^\d{6}$/.test(registerForm.code) : phoneCaptchaMatched.value);
  if (registerType.value === 'email') return registerForm.email.includes('@') && emailChallenge.value && /^\d{6}$/.test(registerForm.emailCode);
  return /^[a-zA-Z][a-zA-Z0-9]{2,31}$/.test(registerForm.username) && usernameCaptchaMatched.value;
});
const canSetRegisterPassword = computed(() => {
  return pendingRegistration.mobile
    && pendingRegistration.resetCode
    && registerPasswordForm.password.length >= 1
    && registerPasswordForm.password === registerPasswordForm.confirmPassword;
});
const phoneCaptchaStatus = computed(() => '');
const phoneCaptchaMatched = computed(() => Boolean(phoneCaptcha.value) && /^\d{4}$/.test(registerForm.code));
const phoneCaptchaClass = computed(() => getCaptchaClass(phoneCaptchaStatus.value));
const usernameCaptchaStatus = computed(() => '');
const usernameCaptchaMatched = computed(() => Boolean(usernameCaptcha.value) && /^\d{4}$/.test(registerForm.usernameCode));
const usernameCaptchaClass = computed(() => getCaptchaClass(usernameCaptchaStatus.value));
watch(visibleRegisterMethods, methods => {
  if (!methods.some(item => item.key === registerType.value)) {
    registerType.value = methods[0]?.key || '';
  }
});
watch(() => [registerForm.phone, registerForm.phoneCountryCode], () => { phoneCaptcha.value='';phoneCaptchaImage.value='';registerForm.code=''; });
watch(() => registerForm.email, () => { emailChallenge.value='';registerForm.emailCode=''; });
watch(() => registerForm.username, () => { usernameCaptcha.value='';usernameCaptchaImage.value='';registerForm.usernameCode=''; });
watch(() => session.error, value => {
  if (value) {
    session.errorKey = '';
  }
});

onMounted(() => {
  loadPlatformConfig();
  loadRegisterConfig();
  restorePendingRegistration();
});

async function requestCode() {
  info.value = '';
  sendingCode.value = true;
  try {
    await imClient.requestAuthCode(mobile.value);
    info.value = '验证码已发送；本地测试也可以直接使用 666666';
  } catch (error) {
    session.error = error.message || '验证码发送失败';
  } finally {
    sendingCode.value = false;
  }
}

function registrationRecipient() {
  if (registerType.value === 'phone') return `+${registerForm.phoneCountryCode || '86'}${registerForm.phone.replace(/\s/g, '')}`;
  return registerType.value === 'email' ? registerForm.email : registerForm.username;
}
async function issueRegistrationChallenge(method, graphic) {
  info.value='';session.error='';sendingCode.value=true;
  try {
    const recipient=method==='phone' ? `+${registerForm.phoneCountryCode || '86'}${registerForm.phone.replace(/\s/g, '')}` : method==='email' ? registerForm.email : registerForm.username;
    const data=await registrationRequest({action:graphic?'captcha':'send_code',method,recipient});
    // Ignore a response if the account was edited while sending.
    const current=method==='phone' ? `+${registerForm.phoneCountryCode || '86'}${registerForm.phone.replace(/\s/g, '')}` : method==='email' ? registerForm.email : registerForm.username;
    if(recipient!==current)return;
    if(method==='phone'){phoneCaptcha.value=data.challenge.id;phoneCaptchaImage.value=data.challenge.image||'';registerForm.code='';}
    else if(method==='email'){emailChallenge.value=data.challenge.id;registerForm.emailCode='';}
    else{usernameCaptcha.value=data.challenge.id;usernameCaptchaImage.value=data.challenge.image;registerForm.usernameCode='';}
    if(!graphic)info.value=data.message;
  }catch(error){session.error=error.message;}finally{sendingCode.value=false;}
}
function requestRegisterCode(){return issueRegistrationChallenge('phone',false);}
function requestEmailCode(){return issueRegistrationChallenge('email',false);}

function submit() {
  if (mode.value === 'password') {
    session.loginWithPassword(mobile.value, password.value);
    return;
  }
  session.loginWithCode(mobile.value, code.value);
}

function setLoginMode(nextMode) {
  session.error = '';
  session.errorKey = '';
  info.value = '';
  mode.value = nextMode;
}

function normalizeUsernameCaptchaInput() {
  registerForm.usernameCode = registerForm.usernameCode.replace(/\D/g, '').slice(0, 4);
}

function normalizePhoneCaptchaInput() {
  registerForm.code = registerForm.code.replace(/\D/g, '').slice(0, 4);
}

function normalizeRegisterCountryCode() {
  registerForm.phoneCountryCode = registerForm.phoneCountryCode.replace(/\D/g, '').slice(0, 6);
}

function showPhoneCaptcha(){return issueRegistrationChallenge('phone',true);}
function showUsernameCaptcha(){return issueRegistrationChallenge('username',true);}

function getCaptchaClass(status) {
  return {
    'captcha-input-valid': status === 'valid',
    'captcha-input-invalid': status === 'invalid'
  };
}

async function submitRegister() {
  if (!canRegisterSubmit.value || registering.value) return;
  session.error='';info.value='';registering.value=true;
  const method=registerType.value;
  try {
    const recipient=registrationRecipient();
    const challenge_id=method==='phone'?phoneCaptcha.value:method==='email'?emailChallenge.value:usernameCaptcha.value;
    const code=method==='phone'?registerForm.code:method==='email'?registerForm.emailCode:registerForm.usernameCode;
    const data=await registrationRequest({action:'register',method,recipient,challenge_id,code,password:registerForm.password,area_code:`+${registerForm.phoneCountryCode||'86'}`});
    mobile.value=method==='phone'?formatPhoneForApi(registerForm.phoneCountryCode,registerForm.phone):recipient;
    password.value='';registerForm.password='';registerForm.confirmPassword='';mode.value='password';view.value='login';info.value=data.message;
  }catch(error){
    session.error=error.message;
    if(method==='username'){usernameCaptcha.value='';usernameCaptchaImage.value='';registerForm.usernameCode='';}
    if(method==='phone'&&!mobileRealVerify.value){phoneCaptcha.value='';phoneCaptchaImage.value='';registerForm.code='';}
  }finally{registering.value=false;}
}

async function submitRegisterPassword() {
  session.error = '';
  info.value = '';
  if (!pendingRegistration.mobile || !pendingRegistration.resetCode) {
    session.error = '注册状态已失效，请重新注册';
    return;
  }
  if (!registerPasswordForm.password || !registerPasswordForm.confirmPassword) {
    session.error = '请填写完整密码';
    return;
  }
  if (registerPasswordForm.password !== registerPasswordForm.confirmPassword) {
    session.error = '两次输入的密码不一致';
    return;
  }

  settingRegisterPassword.value = true;
  try {
    await session.resetPasswordWithCode({
      mobile: pendingRegistration.mobile,
      resetCode: pendingRegistration.resetCode,
      newPassword: registerPasswordForm.password
    });
    await session.loginWithPassword(pendingRegistration.mobile, registerPasswordForm.password);
    if (!session.error) {
      clearPendingRegistration();
    }
  } catch (error) {
    session.error = error.message || '密码设置失败';
  } finally {
    settingRegisterPassword.value = false;
  }
}

function formatPhoneForApi(countryCode, phone) {
  const digits = String(countryCode || '86').replace(/\D/g, '') || '86';
  const normalizedCode = `+${digits}`;
  const normalizedPhone = String(phone || '').replace(/\s/g, '');
  if (normalizedCode === '+86') {
    return normalizedPhone;
  }
  return `${normalizedCode}${normalizedPhone}`;
}

async function loadRegisterConfig() {
  registrationLoading.value=true;
  try {
    const response=await registrationRequest();
    registerMethods.value=response.data.methods;
    mobileRealVerify.value=Boolean(response.data.mobile_real_verify);
  }catch(error){registerMethods.value={phone:false,email:false,username:false};session.error=error.message;}
  finally{registrationLoading.value=false;}
}

async function loadPlatformConfig() { await loadProjectBrand(); }

function handleBrandLogoError() { projectBrand.icon_url = defaultPlatformLogo; }

function clearRegisterPhoneError() {
  registerPhoneError.value = '';
}

function clearPendingRegistration() {
  pendingRegistration.mobile = '';
  pendingRegistration.result = null;
  pendingRegistration.resetCode = '';
  registerPasswordForm.password = '';
  registerPasswordForm.confirmPassword = '';
  sessionStorage.removeItem(pendingRegistrationStorageKey);
}

function setPendingRegistration(phone, result) {
  pendingRegistration.mobile = phone;
  pendingRegistration.result = result;
  pendingRegistration.resetCode = String(result.resetCode || '');
  sessionStorage.setItem(pendingRegistrationStorageKey, JSON.stringify({
    appServer: appConfig.appServer,
    mobile: pendingRegistration.mobile,
    resetCode: pendingRegistration.resetCode,
    result
  }));
}

function restorePendingRegistration() {
  try {
    const pending = JSON.parse(sessionStorage.getItem(pendingRegistrationStorageKey) || 'null');
    if (!pending || pending.appServer !== appConfig.appServer || !pending.mobile || !pending.resetCode) {
      return;
    }
    pendingRegistration.mobile = pending.mobile;
    pendingRegistration.result = pending.result || null;
    pendingRegistration.resetCode = String(pending.resetCode || '');
    registerPasswordForm.password = '';
    registerPasswordForm.confirmPassword = '';
    view.value = 'setPassword';
  } catch (error) {
    sessionStorage.removeItem(pendingRegistrationStorageKey);
  }
}

function openRegister() {
  session.error = '';
  info.value = '';
  clearRegisterPhoneError();
  clearPendingRegistration();
  registerForm.phone = registerForm.phone || (/^[0-9]+$/.test(mobile.value) ? mobile.value : '');
  registerForm.code = '';
  phoneCaptcha.value = '';
  phoneCaptchaImage.value = '';
  view.value = 'register';
  loadRegisterConfig();
}

function backToLogin() {
  session.error = '';
  info.value = '';
  clearRegisterPhoneError();
  clearPendingRegistration();
  mode.value = 'password';
  view.value = 'login';
}
</script>
